import { css } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { UserOnboardingResource } from "@octopusdeploy/octopus-server-client";
import { useRef } from "react";
import * as React from "react";
import type { StepEditorEvent } from "~/analytics/Analytics";
import { Action } from "~/analytics/Analytics";
import styles from "~/areas/projects/components/Process/Common/style.module.less";
import type { RunOn } from "~/areas/projects/components/Process/types";
import { ExecutionLocation } from "~/areas/projects/components/Process/types";
import { TargetTagsProcessEditorContextualHelp } from "~/components/ContextualHelp/ContextualHelpSnippets";
import { TargetTagMultiSelect } from "~/components/MultiSelect/TargetTagMultiSelect";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import type { FocusableComponent } from "~/components/VirtualListWithKeyboard/FocusableComponent";
import DelegatedFormSection from "~/components/form/Sections/DelegatedFormSection";
import { Note } from "~/components/form/index";
import ParseHelper from "~/utils/ParseHelper/index";
export type PlatformHubTargetRolesFormSectionProps = {
    projectSlug: string;
    expandedByDefault: boolean;
    title?: string;
    availableRoles: string[];
    targetRoles: string | undefined;
    onTargetRolesChanged: (roles: string[]) => void;
    runOn: RunOn;
    errorMessage: string | undefined;
    analyticsStepEditorDispatch: (name: string, event: Omit<StepEditorEvent, "stepTemplate">) => void;
    isKubernetesStep: boolean;
    userOnboarding: UserOnboardingResource | null;
    doBusyTask: (action: () => Promise<void>) => Promise<boolean>;
    analyticsArea: string;
    stepTemplateName: string;
    isGuidedSetup: boolean;
};
const RunOnRolesHelp = (props: {
    isKubernetesStep: boolean;
    isGuidedSetup: boolean;
    executionLocation: ExecutionLocation;
}) => {
    if (props.isGuidedSetup && props.isKubernetesStep) {
        return <span>Use target tags to select the Kubernetes clusters this step will deploy to.</span>;
    }
    return <span>This step will deploy to {props.isKubernetesStep ? "Kubernetes clusters" : "deployment targets"} with any of these matching tags.</span>;
};
type SuggestedTargetRolesProps = Pick<PlatformHubTargetRolesFormSectionProps, "targetRoles" | "onTargetRolesChanged" | "analyticsStepEditorDispatch"> & {
    projectSlug: string;
    isKubernetesStep: boolean;
    onClick: () => void;
};
export const SuggestedTargetRoles = (props: SuggestedTargetRolesProps) => {
    const { targetRoles, onTargetRolesChanged, analyticsStepEditorDispatch, projectSlug, isKubernetesStep, onClick } = props;
    const suggestedRoles = getSuggestedTargetTags(projectSlug, isKubernetesStep);
    const onSelectSuggestedRole = (e: React.MouseEvent<HTMLAnchorElement>, targetName: string) => {
        e.preventDefault();
        const prevTargets = ParseHelper.parseCSV(targetRoles || "");
        if (prevTargets.includes(targetName)) {
            return;
        }
        analyticsStepEditorDispatch?.("Select Suggested Target", { action: Action.Select, resource: "Target Role", value: targetName });
        onTargetRolesChanged(prevTargets.concat(targetName));
        onClick();
    };
    return (<ul className={styles.suggestedTargetRoles}>
            <li key={suggestedRoles}>
                <a href="#" onClick={(e) => onSelectSuggestedRole(e, suggestedRoles)}>
                    {suggestedRoles}
                </a>
            </li>
        </ul>);
};
export const PlatformHubTargetRolesFormSection = (props: PlatformHubTargetRolesFormSectionProps) => {
    const { projectSlug, title, expandedByDefault, targetRoles, runOn, onTargetRolesChanged, errorMessage, availableRoles, isKubernetesStep } = props;
    const multiSelectRef = useRef<FocusableComponent | null>(null);
    return (<DelegatedFormSection isExpandedByDefault={expandedByDefault} errorKey="Octopus.Action.TargetRoles" help={<RunOnRolesHelp isKubernetesStep={props.isKubernetesStep} isGuidedSetup={props.isGuidedSetup} executionLocation={runOn.executionLocation}></RunOnRolesHelp>} contextualHelp={<TargetTagsProcessEditorContextualHelp />} accessibleName={"Target Tags"} title={<span className={disabledStyles.disabledText}>{title || "Target Tags"}</span>}>
            <Note className={disabledStyles.disabledText}>
                Learn more about <ExternalLink href="TargetRoles">target tags</ExternalLink>.
            </Note>
            <Tooltip content={"This field requires values specific to the project."} position={"bottom"} display={"block"}>
                <TargetTagMultiSelect onChange={onTargetRolesChanged} value={ParseHelper.parseCSV(targetRoles || "")} label={runOn.executionLocation === ExecutionLocation.DeploymentTarget ? `Runs on targets with tags (type to add new)` : `On behalf of target tags (type to add new)`} validate={(roles) => (roles.length === 0 ? "Please enter one or more tags" : "")} error={errorMessage} items={availableRoles} canAdd={true} empty={<div className={targetRolesEmptyStyles}>
                            {targetRoles ? (<>Add a new target tag</>) : (<>
                                    Create your first target tag, for example:{" "}
                                    {<SuggestedTargetRoles {...props} projectSlug={projectSlug} isKubernetesStep={isKubernetesStep} onClick={() => {
                        if (multiSelectRef.current)
                            multiSelectRef.current.focus();
                    }}/>}
                                </>)}
                        </div>} accessibleName={"Target tags selector"} analyticsArea="Deployment Process Editor" multiSelectRef={(component) => (multiSelectRef.current = component)} disabled={true}/>
            </Tooltip>
            <Note className={disabledStyles.disabledText}>You need at least one tag to select matching targets.</Note>
        </DelegatedFormSection>);
};
const disabledStyles = {
    disabledText: css({
        color: `${themeTokens.color.text.disabled} !important`,
    }),
};
export const getSuggestedTargetTags = (projectSlug: string, isKubernetesStep: boolean) => {
    return `${projectSlug}-${isKubernetesStep ? "clusters" : "targets"}`;
};
const targetRolesEmptyStyles = css({
    font: text.body.regular.medium,
    padding: `${space[6]} ${space[2]}`,
    lineHeight: "1.5rem",
});
